import { useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X } from "lucide-react";
import { PdfField } from "../types";
import { DEFAULT_FONT_SIZE } from "./common";
import { safeParseDecimal } from "@/lib/api/common";
import { Checkbox } from "@/components/ui/checkbox";
import { acceptedFieldsOptions, AcceptedAchFields } from "@/lib/api/ach";
import { SignatureFieldType } from "@/lib/api/signingContracts";

interface FieldFormProps {
  field: PdfField;
  onSave: (updates: Partial<PdfField>) => void;
  onCancel: () => void;
  availableFields: AcceptedAchFields[];
}

export function FieldForm({ field, onSave, onCancel, availableFields }: FieldFormProps) {
  const [formData, setFormData] = useState({
    selectedFieldType: field.field_id as AcceptedAchFields,
    field_id: field.field_id,
    field_key: (field as any).field_key || acceptedFieldsOptions.find(opt => opt.value === field.field_id)?.label || field.field_id,
    field_type: field.field_type,
    system_field_type: field.system_field_type || 'system_reference_field',
    field_dest_type: field.field_dest_type,

    position_data: {
      x: safeParseDecimal({})(field.position_data.x).times(100).toNumber(),
      y: safeParseDecimal({})(field.position_data.y).times(100).toNumber(),
      width: safeParseDecimal({})(field.position_data.width).times(100).toNumber(),
      height: safeParseDecimal({})(field.position_data.height).times(100).toNumber(),

      text_size: field.position_data.text_size || DEFAULT_FONT_SIZE,
      page: field.position_data.page || 1,
    },

    is_required: field.is_required ?? true,
    is_readonly: field.is_readonly ?? false,

    freeform_value: field.freeform_value,
    value: field.value || '',
  });

  const availableFieldOptions = acceptedFieldsOptions.filter(option => 
    availableFields.includes(option.value) || option.value === formData.selectedFieldType
  );

  const getDefaultFieldDestType = (fieldId: string): "signature" | "date_signed" | "text" | "attachment" | undefined => {
    const lowerFieldId = fieldId;

    if (/date/i.test(lowerFieldId)) {
      return "date_signed";
    }

    if (/signature/i.test(lowerFieldId)) {
      return "signature";
    }

    if (/attachment/i.test(lowerFieldId)) {
      return "attachment";
    }
    return "text";
  };

  const handleFieldSelection = (selectedField: string) => {
    const typedField = selectedField as AcceptedAchFields;
    const isSignatureField = [
      AcceptedAchFields.CUSTOMER_SIGNATURE,
      AcceptedAchFields.CUSTOMER_DATE,
      AcceptedAchFields.LENDER_SIGNATURE,
      AcceptedAchFields.LENDER_DATE,
      AcceptedAchFields.CUSTOMER_INSTITUTION_NAME,
      AcceptedAchFields.CUSTOMER_BANK_NUMBER,
      AcceptedAchFields.CUSTOMER_BRANCH_NUMBER,
      AcceptedAchFields.CUSTOMER_ACCOUNT_CURRENCY,
      AcceptedAchFields.CUSTOMER_ROUTING_NUMBER,
      AcceptedAchFields.CUSTOMER_ACCOUNT_NUMBER,
      AcceptedAchFields.LENDER_PRINTED_NAME,
      AcceptedAchFields.LENDER_INSTITUTION,
      AcceptedAchFields.LENDER_TITLE,
      AcceptedAchFields.LENDER_EMAIL,
      AcceptedAchFields.VOIDED_CHECK_ATTACHMENT,
    ].includes(typedField);
    const isSystemField = !isSignatureField;
    const selectedOption = acceptedFieldsOptions.find(option => option.value === typedField);
    const text_size = generateTextSizeFrom(typedField);
    const preview_value = generatePreviewValue(typedField);
    const defaultFieldDestType = isSignatureField ? getDefaultFieldDestType(typedField) : undefined;

    setFormData(prev => ({
      ...prev,
      value: preview_value,
      freeform_value: "",
      position_data: {
        ...prev.position_data,
        text_size,
      },
      selectedFieldType: typedField,
      field_id: typedField,
      field_key: selectedOption?.label || typedField,
      field_type: isSignatureField ? 'signature' : 'system',
      system_field_type: isSystemField ? 'enum_fixed_value' as const : prev.system_field_type,
      field_dest_type: defaultFieldDestType,
      is_required: isSignatureField ? true : prev.is_required,
    }));
  };
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.selectedFieldType) {
      newErrors.selectedFieldType = "Please select a field type";
    }

    if (formData.field_type === 'system' && (formData.position_data.text_size < 6 || formData.position_data.text_size > 72)) {
      newErrors.text_size = "Font size must be between 6 and 72";
    }

    if (formData.position_data.x < 0 || formData.position_data.x > 100) {
      newErrors.x = "X position must be between 0 and 100%";
    }

    if (formData.position_data.y < 0 || formData.position_data.y > 100) {
      newErrors.y = "Y position must be between 0 and 100%";
    }

    if (formData.position_data.width <= 0 || formData.position_data.width > 100) {
      newErrors.width = "Width must be between 1 and 100%";
    }

    if (formData.position_data.height <= 0 || formData.position_data.height > 100) {
      newErrors.height = "Height must be between 1 and 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const updates: Partial<PdfField> = {
      field_id: formData.field_id,
      field_key: formData.field_key,
      field_type: formData.field_type,
      system_field_type: formData.system_field_type,
      field_dest_type: formData.field_dest_type,
      position_data: {
        x: formData.position_data.x / 100,
        y: formData.position_data.y / 100,
        width: formData.position_data.width / 100,
        height: formData.position_data.height / 100,
        page: formData.position_data.page,
        text_size: formData.position_data.text_size,
      },
      freeform_value: formData.freeform_value,
      value: formData.value,
      is_required: formData.is_required,
      is_readonly: formData.is_readonly,
    };

    onSave(updates);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave();
    } else if (event.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="p-3 border-2 border-blue-300 rounded-lg bg-blue-50 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-sm">Edit Field</h4>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={handleSave}>
            <Check className="w-3 h-3 text-green-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-3 h-3 text-red-600" />
          </Button>
        </div>
      </div>
      {formData.field_type === 'signature' && (
        <>
          <div className="flex items-center justify-between">
            <div className="grid gap-2">
              <Label className="cursor-pointer">
                Required
              </Label>
              <Checkbox
                checked={formData.is_required}
                onCheckedChange={(value: boolean) => setFormData(prev => ({ ...prev, is_required: value }))}
                className="rounded border-gray-300"
              />
            </div>
            <div className="grid gap-2">
              <Label className="cursor-pointer">
                Readonly
              </Label>
              <Checkbox
                checked={formData.is_readonly}
                onCheckedChange={(value: boolean) => setFormData(prev => ({ ...prev, is_readonly: value }))}
                className="rounded border-gray-300"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="field_dest_type">Destination Type</Label>
            <Select
              value={formData.field_dest_type || "text"}
              onValueChange={(value: "signature" | "date_signed" | "text") =>
                setFormData(prev => ({ ...prev, field_dest_type: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select destination type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="signature">Signature</SelectItem>
                <SelectItem value="date_signed">Date</SelectItem>
                <SelectItem value="attachment">Attachment</SelectItem>
                <SelectItem value="text">Text</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      <div className="space-y-3 md:space-y-4">
        <div className="flex flex-col md:grid md:grid-cols-3 gap-4">
          <div className="flex flex-col">
            <Label className="text-xs">Field Type</Label>
            <Combobox
              placeholder="Select field type..."
              options={availableFieldOptions}
              value={formData.selectedFieldType}
              onValueChange={handleFieldSelection}
              dwimOnChange
              className={`mt-1 ${errors.selectedFieldType ? 'border-red-500' : ''}`}
            />
            {errors.selectedFieldType && (
              <p className="text-xs text-red-500 mt-1">{errors.selectedFieldType}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formData.field_type === 'system'
                ? 'Text will be inserted by the system'
                : 'Field for external signature/date input'
              }
            </p>
          </div>
        </div>

        {/* Position and Size Controls */}
        <div>
          <Label className="text-xs font-medium mb-2 block">Position & Size</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-gray-600">X (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.position_data.x.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, x: parseFloat(e.target.value) || 0 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.x ? 'border-red-500' : ''}`}
              />
              {errors.x && (
                <p className="text-xs text-red-500 mt-1">{errors.x}</p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-600">Y (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.position_data.y.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, y: parseFloat(e.target.value) || 0 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.y ? 'border-red-500' : ''}`}
              />
              {errors.y && (
                <p className="text-xs text-red-500 mt-1">{errors.y}</p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-600">Width (%)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                step="0.1"
                value={formData.position_data.width.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, width: parseFloat(e.target.value) || 1 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.width ? 'border-red-500' : ''}`}
              />
              {errors.width && (
                <p className="text-xs text-red-500 mt-1">{errors.width}</p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-600">Height (%)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                step="0.1"
                value={formData.position_data.height.toFixed(1)}
                onChange={(e) => setFormData(prev => ({ ...prev, position_data: { ...prev.position_data, height: parseFloat(e.target.value) || 1 } }))}
                onKeyDown={handleKeyPress}
                className={`mt-1 ${errors.height ? 'border-red-500' : ''}`}
              />
              {errors.height && (
                <p className="text-xs text-red-500 mt-1">{errors.height}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pt-2 border-t">
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} className="flex-1">
            Save Changes
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function generateTextSizeFrom(field_id: AcceptedAchFields) {
  return DEFAULT_FONT_SIZE;
}

function generatePreviewValue(field_id: AcceptedAchFields) {
  if (AcceptedAchFields.CUSTOMER_DATE === field_id || AcceptedAchFields.LENDER_DATE === field_id) {
    return moment().format("YYYY-MM-DD");
  }
  return '';
}

